import React from 'react';
import { EditableField } from './EditableField';

interface LineItem {
  id: string;
  description: string;
  quantity: number;
  rate: number;
}

interface InvoiceCalculationsProps {
  items: LineItem[];
  taxRate: number;
  discountAmount: number;
  discountType: 'None' | 'Percentage' | 'Fixed';
  discountPercentage?: number;
  amountPaid: number;
  onTaxRateChange: (rate: number) => void;
  onDiscountAmountChange: (amount: number) => void;
  onDiscountTypeChange: (type: 'None' | 'Percentage' | 'Fixed') => void;
  onDiscountPercentageChange: (percentage: number) => void;
  onAmountPaidChange: (amount: number) => void;
  clientLocationName?: string;
  isUsingLocationTaxRate?: boolean;
}

export const InvoiceCalculations: React.FC<InvoiceCalculationsProps> = ({
  items,
  taxRate,
  discountAmount,
  discountType,
  discountPercentage,
  amountPaid,
  onTaxRateChange,
  onDiscountAmountChange,
  onDiscountTypeChange,
  onDiscountPercentageChange,
  onAmountPaidChange,
  clientLocationName,
  isUsingLocationTaxRate
}) => {
  const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
  const calculatedDiscountAmount = discountType === 'Percentage' && discountPercentage 
    ? subtotal * (discountPercentage / 100)
    : discountAmount;
  const subtotalAfterDiscount = subtotal - calculatedDiscountAmount;
  const taxAmount = subtotalAfterDiscount * taxRate;
  const total = subtotalAfterDiscount + taxAmount;
  const amountDue = total - amountPaid;

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center py-2">
        <span className="text-gray-600">Subtotal:</span>
        <span className="font-medium">${subtotal.toFixed(2)}</span>
      </div>

      {/* Discount Section */}
      <div className="space-y-2 border-l-2 border-gray-200 pl-3">
        <div className="flex justify-between items-center py-1">
          <div className="flex items-center space-x-2">
            <span className="text-gray-600">Discount</span>
            <select
              value={discountType}
              onChange={(e) => onDiscountTypeChange(e.target.value as 'None' | 'Percentage' | 'Fixed')}
              className="text-sm border border-gray-300 rounded px-2 py-1"
            >
              <option value="None">None</option>
              <option value="Percentage">Percentage</option>
              <option value="Fixed">Fixed Amount</option>
            </select>
            {discountType === 'Percentage' && (
              <EditableField
                value={discountPercentage?.toFixed(1) || '0'}
                onChange={(value) => {
                  const percentage = parseFloat(value) || 0;
                  onDiscountPercentageChange(percentage);
                }}
                placeholder="0.0"
                className="w-12 text-center text-sm"
              />
            )}
            {discountType === 'Fixed' && (
              <EditableField
                value={discountAmount.toFixed(2)}
                onChange={(value) => {
                  const amount = parseFloat(value) || 0;
                  onDiscountAmountChange(amount);
                }}
                placeholder="0.00"
                className="w-16 text-right text-sm"
              />
            )}
          </div>
          <span className="font-medium text-red-600">
            -${calculatedDiscountAmount.toFixed(2)}
          </span>
        </div>
        <div className="flex justify-between items-center py-1">
          <span className="text-gray-600 text-sm">After Discount:</span>
          <span className="font-medium">${subtotalAfterDiscount.toFixed(2)}</span>
        </div>
      </div>

      <div className="flex justify-between items-center py-2">
        <div className="flex items-center space-x-2">
          <span className="text-gray-600">Tax</span>
          <EditableField
            value={`${(taxRate * 100).toFixed(2)}%`}
            onChange={(value) => {
              const rate = parseFloat(value.replace('%', '')) / 100 || 0;
              onTaxRateChange(rate);
            }}
            placeholder="0.00%"
            className="w-16 text-center text-sm"
          />
        </div>
        <span className="font-medium">${taxAmount.toFixed(2)}</span>
      </div>

      <div className="flex justify-between items-center py-2 border-t border-gray-200">
        <span className="text-gray-600 font-semibold">Total:</span>
        <span className="font-bold text-lg">${total.toFixed(2)}</span>
      </div>

      <div className="flex justify-between items-center py-2">
        <span className="text-gray-600">Amount Paid:</span>
        <EditableField
          value={amountPaid.toFixed(2)}
          onChange={(value) => {
            const amount = parseFloat(value) || 0;
            onAmountPaidChange(amount);
          }}
          placeholder="0.00"
          className="w-20 text-right"
        />
      </div>

      <div className="flex justify-between items-center py-2 border-t border-gray-200 bg-gray-50 rounded px-3">
        <span className="text-gray-700 font-semibold">Amount Due:</span>
        <span className={`font-bold text-lg ${amountDue > 0 ? 'text-red-600' : 'text-green-600'}`}>
          ${amountDue.toFixed(2)}
        </span>
      </div>
    </div>
  );
};
